import React from 'react';
import { Insight } from '@/api/insightsApi';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { chartColors } from './chartColors';
import { CustomTooltip } from './CustomTooltip';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { useRechartsTheme } from '@/hooks/useRechartsTheme';

interface ExcellentProgressVisualizationProps {
  insight: Insight;
}

export const ExcellentProgressVisualization: React.FC<ExcellentProgressVisualizationProps> = ({ insight }) => {
  const { theme } = useAppTheme();
  const chartTheme = useRechartsTheme();
  // Parse description to extract completion rate
  const desc = insight.description || '';
  const completionMatch = desc.match(/([\d.]+)%/);
  const completion = completionMatch ? Number.parseFloat(completionMatch[1]) : 80;
  const remaining = 100 - completion;

  const progressData = [
    { name: 'Completed', value: completion, color: chartColors.success },
    { name: 'Remaining', value: remaining, color: chartColors.primary },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Completion Rate</div>
          <div className="text-2xl font-bold text-green-400">{completion.toFixed(1)}%</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Remaining</div>
          <div className="text-2xl font-bold text-nb-accent-blue">{remaining.toFixed(1)}%</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Status</div>
          <div className="text-2xl font-bold text-green-400">On Track</div>
        </div>
      </div>

      {/* Progress Chart */}
      <div className={`rounded-lg p-4 border ${
        theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <h4 className={`text-sm font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Project Progress</h4>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <Pie
              data={progressData}
              cx="50%"
              cy="45%"
              labelLine={false}
              label={false}
              outerRadius={70}
              fill="#8884d8"
              dataKey="value"
            >
              {progressData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              content={<CustomTooltip formatter={(value: number, name?: string) => [
                `${value.toFixed(1)}%`,
                name || 'Value'
              ]} />}
            />
            <Legend 
              verticalAlign="bottom"
              height={60}
              wrapperStyle={{ 
                color: chartTheme.textColor,
                paddingTop: '10px',
                fontSize: '12px'
              }}
              iconType="circle"
              formatter={(value: string) => (
                <span style={{ color: chartTheme.textColor }}>
                  {value}: {progressData.find(d => d.name === value)?.value.toFixed(1) || 0}%
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};



